import { Controller } from "@hotwired/stimulus";
import MultiSelect from "@dotburo/multi-select";

export default class ProductGroupProfileController extends Controller {
  static targets = [
    "productIdSelect",
    "resourceTypeSelect",
    "resourceSelect",
    "resourceSkuSelect",
    "defaultResourceSkuSelect",
    "profileContainer",
    "groupedResource",
    "template",
    "noItemsMessage",
    "addGroupButton",
    "loadingOverlay",
  ];

  static values = {
    resourceTypesUrl: String,
    resourcesByTypeUrl: String,
    resourceSkusByResourceUrl: String,
    selectOptionText: { type: String, default: "Select an Option" },
    loadingText: { type: String, default: "Loading ..." },
    loadingErrorText: { type: String, default: "Error while loading data" },
    retryText: { type: String, default: "Retry" },
    resourceTypeValidationMessage: {
      type: String,
      default: "Resource type is required",
    },
    resourcesValidationMessage: {
      type: String,
      default: "At least one resource is required",
    },
    resourceSkusValidationMessage: {
      type: String,
      default: "At least one resource skus is required",
    },
    defaultResourceSkuValidationMessage: {
      type: String,
      default: "Default resource sku is required",
    },
    formState: {
      type: Object,
      default: null,
    },
  };

  async connect() {
    this.state = {};

    await this.loadData();

    this.profileContainerTarget
      .closest("form")
      .addEventListener("submit", this.handleFormSubmit.bind(this));

    this.initializeRows();

    this.observer = null;

    this.toggleNoItemsMessage();
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    const multiSelectContainers = this.element.querySelectorAll(
      '[data-name="resources"], [data-name="resourceSkus"]'
    );

    for (let i = 0; i < multiSelectContainers.length; i += 1) {
      const container = multiSelectContainers[i];
      if (container.multiSelectInstance) {
        try {
          container.multiSelectInstance.remove();
        } finally {
          container.multiSelectInstance = null;
        }
      }
    }

    const defaultSkuElements = this.element.querySelectorAll(
      '[data-product-group-profile-target="defaultResourceSkuSelect"]'
    );

    for (let i = 0; i < defaultSkuElements.length; i += 1) {
      const el = defaultSkuElements[i];
      /* eslint-disable no-underscore-dangle */
      if (el._validationObserver) {
        el._validationObserver.disconnect();
        delete el._validationObserver;
      }
      /* eslint-enable no-underscore-dangle */
    }
  }

  async loadData() {
    this.resourceTypes = [];
    this.resourcesByType = [];
    this.resourceSkusByResource = [];

    const productId = this.productIdSelectTarget.value;
    if (!productId || productId === "") return;

    this.showLoadingState();

    try {
      await this.loadResourceTypes(productId);
      await this.loadAllResourcesByType(productId);
      await this.loadAllResourceSkusByResource(productId);

      const id = this.element.dataset.configId;
      if (id && parseInt(id, 10) > 0) {
        if (this.formStateValue && this.formStateValue.grouped_resources) {
          if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
          }
          this.loadState(this.formStateValue);
        } else {
          const response = await fetch(
            `/api/product_group_profiles/${id}/grouped_resources`
          );
          if (response.ok) {
            const data = await response.json();
            if (this.observer) {
              this.observer.disconnect();
              this.observer = null;
            }
            this.loadState(data);
          }
        }
      } else {
        this.resetGroupControls();
      }

      this.hideLoadingState();
      this.observeContainer();
    } catch (error) {
      this.showErrorState();
    }
  }

  showLoadingState() {
    this.loadingOverlayTarget.classList.remove("hidden");
    this.profileContainerTarget.classList.add("relative");
    this.addGroupButtonTarget.disabled = true;
  }

  hideLoadingState() {
    this.loadingOverlayTarget.classList.add("hidden");
    this.addGroupButtonTarget.disabled = false;
  }

  showErrorState() {
    this.loadingOverlayTarget.innerHTML = `<div class="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center">
        <div class="text-center">
          <div class="text-red-600 text-lg font-semibold mb-2">${this.loadingErrorTextValue}</div>
          <button class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                  data-action="click->product-group-profile#retryLoading">
            ${this.retryTextValue}
          </button>
        </div>
      </div>
    `;

    this.addGroupButtonTarget.disabled = true;
  }

  retryLoading() {
    this.showLoadingState();
    this.loadData();
  }

  resetGroupControls() {
    this.resourceTypeSelectTargets.forEach((select) => {
      if (select) {
        // eslint-disable-next-line no-param-reassign
        select.innerHTML = `<option value="" selected disabled>${
          this.selectOptionTextValue
        }</option>${this.resourceTypes
          .map(
            (resource) =>
              `<option value="${resource.id}">${resource.label}</option>`
          )
          .join("")}`;
      }
    });

    this.resourceSelectTargets.forEach((select) => {
      if (select) {
        this.initMultiSelectWithOptions(select, []);
      }
    });

    this.resourceSkuSelectTargets.forEach((select) => {
      if (select) {
        this.initMultiSelectWithOptions(select, []);
      }
    });

    this.defaultResourceSkuSelectTargets.forEach((select) => {
      if (select) {
        // eslint-disable-next-line no-param-reassign
        select.innerHTML = `<option value="" selected disabled>${this.selectOptionTextValue}</option>`;
      }
    });
  }

  async loadResourceTypes(productId) {
    const url = this.resourceTypesUrlValue.replace(":product_id", productId);
    const response = await fetch(url);
    this.resourceTypes = await response.json();
  }

  async loadAllResourcesByType(productId) {
    const url = this.resourcesByTypeUrlValue.replace(":product_id", productId);
    const response = await fetch(url);
    this.resourcesByType = await response.json();
  }

  async loadAllResourceSkusByResource(productId) {
    const url = this.resourceSkusByResourceUrlValue.replace(
      ":product_id",
      productId
    );
    const response = await fetch(url);
    this.resourceSkusByResource = await response.json();

    this.resourceSkusById = new Map();
    Object.entries(this.resourceSkusByResource).forEach(([, skus]) => {
      skus.forEach((sku) => {
        this.resourceSkusById.set(sku.id, sku);
      });
    });
  }

  initializeRows() {
    const rows = this.profileContainerTarget.querySelectorAll(
      "li.grouped-resource-row"
    );

    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      if (!row.dataset.id) {
        row.dataset.id = `row_${i}`;
      }
      this.initializeRow(row);
    }
  }

  initializeRow(row) {
    const rowId = row.dataset.id;

    if (!rowId || this.state[rowId]) {
      return;
    }

    this.state[rowId] = {
      resources: new Set(),
      resourceSkus: new Set(),
    };

    const resourceTypeEl = row.querySelector(
      '[data-product-group-profile-target="resourceTypeSelect"]'
    );
    if (resourceTypeEl) {
      const clearError = () => {
        const err =
          resourceTypeEl.parentNode.querySelector(".validation-error");
        if (err) err.remove();
      };

      resourceTypeEl.addEventListener("input", clearError);
      resourceTypeEl.addEventListener("change", clearError);
    }

    const defaultSkuEl = row.querySelector(
      '[data-product-group-profile-target="defaultResourceSkuSelect"]'
    );
    if (defaultSkuEl) {
      const clearError = () => {
        const err = defaultSkuEl.parentNode.querySelector(".validation-error");
        if (err) err.remove();
      };

      defaultSkuEl.addEventListener("input", clearError);
      defaultSkuEl.addEventListener("change", clearError);

      const observer = new MutationObserver(() => {
        clearError();
      });
      observer.observe(defaultSkuEl, {
        attributes: true,
        attributeFilter: ["value"],
      });

      // eslint-disable-next-line no-underscore-dangle
      defaultSkuEl._validationObserver = observer;
    }
  }

  removeRow(row) {
    if (!row || !row.dataset) {
      return;
    }

    const rowId = row.dataset.id;
    if (!rowId) {
      return;
    }

    const multiSelectContainers = row.querySelectorAll(
      '[data-name="resources"], [data-name="resourceSkus"]'
    );

    for (let i = 0; i < multiSelectContainers.length; i += 1) {
      const container = multiSelectContainers[i];
      if (container.multiSelectInstance) {
        try {
          container.multiSelectInstance.remove();
        } finally {
          container.multiSelectInstance = null;
        }
      }
    }

    delete this.state[rowId];

    const defaultSkuEl = row.querySelector(
      '[data-product-group-profile-target="defaultResourceSkuSelect"]'
    );

    /* eslint-disable no-underscore-dangle */
    if (defaultSkuEl && defaultSkuEl._validationObserver) {
      defaultSkuEl._validationObserver.disconnect();
      delete defaultSkuEl._validationObserver;
    }
    /* eslint-enable no-underscore-dangle */

    if (row.parentNode) {
      row.parentNode.removeChild(row);
    }

    this.toggleNoItemsMessage();
  }

  removeGroupedResource(event) {
    const row = event.target.closest(".grouped-resource-row");
    if (!row) return;

    this.removeRow(row);
  }

  observeContainer() {
    if (this.observer) return;
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (
            node.nodeType === Node.ELEMENT_NODE &&
            node.dataset &&
            node.dataset.id
          ) {
            this.initializeRow(node);
          }
        });

        mutation.removedNodes.forEach((node) => {
          if (
            node.nodeType === Node.ELEMENT_NODE &&
            node.dataset &&
            node.dataset.id
          ) {
            const rowId = node.dataset.id;
            if (this.state[rowId]) {
              delete this.state[rowId];
            }
          }
        });
      });
    });

    this.observer.observe(this.profileContainerTarget, {
      childList: true,
      subtree: false,
    });
  }

  handleMultiSelectChange(event) {
    if (!event || !event.target) {
      return;
    }

    if (event.detail && typeof event.detail.key !== "undefined") {
      const row = event.target.closest("[data-id]");
      if (!row) {
        return;
      }

      const rowId = row.dataset.id;
      const dropdownName = event.target.dataset?.name;

      if (!rowId || !dropdownName) {
        return;
      }

      if (!this.state[rowId]) {
        this.state[rowId] = {
          resources: new Set(),
          resourceSkus: new Set(),
        };
      }

      const { multiSelectInstance } = event.target;
      if (!multiSelectInstance) {
        return;
      }

      const selectedItems = multiSelectInstance.getCurrent();
      const selectedKeys = selectedItems.map((item) => parseInt(item.key, 10));

      const set = this.state[rowId][dropdownName];
      set.clear();
      selectedKeys.forEach((key) => set.add(key));

      if (dropdownName === "resources") {
        const resourceDropdownEl = row.querySelector(
          '[data-product-group-profile-target="resourceSelect"]'
        );
        if (resourceDropdownEl) {
          const err =
            resourceDropdownEl.parentNode.querySelector(".validation-error");
          if (err) err.remove();
        }

        const resourceSkuDropdown = row.querySelector(".resource-sku-dropdown");

        if (resourceSkuDropdown) {
          const selectedResources = this.state[rowId].resources;
          const availableResourceSkus =
            this.getResourceSkusByResource(selectedResources);

          this.state[rowId].resourceSkus.clear();

          this.initMultiSelectWithOptions(
            resourceSkuDropdown,
            availableResourceSkus,
            [] // No pre-selected items
          );

          this.updateDefaultResourceSkus({ target: resourceSkuDropdown });
        }
      }

      if (dropdownName === "resourceSkus") {
        const resourceSkuDropdownEl = row.querySelector(
          '[data-product-group-profile-target="resourceSkuSelect"]'
        );
        if (resourceSkuDropdownEl) {
          const err =
            resourceSkuDropdownEl.parentNode.querySelector(".validation-error");
          if (err) err.remove();
        }

        this.updateDefaultResourceSkus(event);
      }
    }
  }

  updateDropdownsFromState(rowId) {
    const row = this.profileContainerTarget.querySelector(
      `[data-id="${rowId}"]`
    );
    if (!row || !this.state[rowId]) return;

    row.querySelectorAll("[data-name]").forEach((dropdown) => {
      const dropdownName = dropdown.dataset.name;
      const keys = Array.from(this.state[rowId][dropdownName]);

      const options = dropdown.querySelectorAll("option");
      for (let i = 0; i < options.length; i += 1) {
        const option = options[i];
        option.selected = keys.includes(parseInt(option.value, 10));
      }

      dropdown.dispatchEvent(new Event("change"));
    });
  }

  updateResources(event, selectedResourceIds = []) {
    const resourceTypeId = event.target.value;
    const groupedResourceRow = event.target.closest(".grouped-resource-row");

    if (
      groupedResourceRow &&
      this.profileContainerTarget.contains(groupedResourceRow)
    ) {
      const dropdown = groupedResourceRow.querySelector(".resource-dropdown");
      if (dropdown) {
        const resources = this.getResourcesByType(resourceTypeId);
        this.initMultiSelectWithOptions(
          dropdown,
          resources,
          selectedResourceIds
        );
      }
    }
  }

  updateResourceSkus(event, selectedResourceSkuIds = []) {
    const groupedResourceRow = event.target.closest(".grouped-resource-row");

    if (
      groupedResourceRow &&
      this.profileContainerTarget.contains(groupedResourceRow)
    ) {
      const dropdown = groupedResourceRow.querySelector(
        ".resource-sku-dropdown"
      );
      if (dropdown) {
        const selectedResources =
          this.state[groupedResourceRow.dataset.id].resources;

        const resourceSkus = this.getResourceSkusByResource(selectedResources);
        this.initMultiSelectWithOptions(
          dropdown,
          resourceSkus,
          selectedResourceSkuIds
        );
      }
    }
  }

  updateDefaultResourceSkus(event, selectedId) {
    const groupedResourceRow = event.target.closest(".grouped-resource-row");

    if (
      groupedResourceRow &&
      this.profileContainerTarget.contains(groupedResourceRow)
    ) {
      const dropdown = groupedResourceRow.querySelector(
        ".default-sku-dropdown"
      );
      if (dropdown) {
        const selectedItems =
          this.state[groupedResourceRow.dataset.id].resourceSkus;

        const resources = this.getResourceSkusByIds(selectedItems);

        dropdown.innerHTML = "";

        if (resources.length === 0) {
          dropdown.innerHTML = `<option value="" selected disabled>${this.selectOptionTextValue}</option>`;
        } else {
          dropdown.innerHTML = resources
            .map((resource) => {
              const selected =
                selectedId && selectedId === resource.id ? "selected" : "";
              return `<option ${selected} value="${resource.id}">${resource.name}</option>`;
            })
            .join("");

          if (resources.length === 1) {
            dropdown.value = resources[0].id;
          } else if (!selectedId) {
            dropdown.innerHTML = `<option value="" selected disabled>${this.selectOptionTextValue}</option>${dropdown.innerHTML}`;
          }
        }

        const err = dropdown.parentNode.querySelector(".validation-error");
        if (err) err.remove();
      }
    }
  }

  getResourcesByType(resourceTypeId) {
    return this.resourcesByType[resourceTypeId] || [];
  }

  getResourceSkusByResource(resourceIds) {
    if (resourceIds instanceof Set) {
      const allSkus = new Map();
      Array.from(resourceIds).forEach((resourceId) => {
        const skus = this.resourceSkusByResource[resourceId] || [];
        skus.forEach((sku) => {
          allSkus.set(sku.id, sku);
        });
      });
      return Array.from(allSkus.values());
    }

    return this.resourceSkusByResource[resourceIds] || [];
  }

  getResourceSkusByIds(resourceSkuIds) {
    if (!(resourceSkuIds instanceof Set) && !Array.isArray(resourceSkuIds)) {
      return this.resourceSkusById.get(resourceSkuIds) || null;
    }

    const ids =
      resourceSkuIds instanceof Set
        ? Array.from(resourceSkuIds)
        : resourceSkuIds;

    return ids
      .map((id) => this.resourceSkusById.get(id))
      .filter((sku) => sku !== undefined);
  }

  initializeMultiselect(container, items, selectedItems) {
    if (!container) {
      return;
    }

    // eslint-disable-next-line no-param-reassign
    if (!items || items.length === 0) items = [];

    const selectedItemsAsIntegers = selectedItems.map((id) => parseInt(id, 10));

    const preSelectedItems = items
      .filter((item) => selectedItemsAsIntegers.includes(parseInt(item.id, 10)))
      .map((item) => ({ key: String(item.id), value: item.name })); // Ensure key is string

    try {
      if (container.multiSelectInstance) {
        container.multiSelectInstance.remove();
      }

      const allItems = items.map((item) => ({
        key: String(item.id),
        value: item.name,
      }));

      // eslint-disable-next-line no-param-reassign
      container.multiSelectInstance = new MultiSelect(container, {
        placeholder: this.selectOptionTextValue,
        items: allItems,
        current: preSelectedItems,
        maxHeight: 300,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error initializing MultiSelect:", error);
    }
  }

  initMultiSelectWithOptions(dropdown, resources, selectedIds = []) {
    if (!dropdown) {
      return;
    }

    if (dropdown.multiSelectInstance) {
      try {
        const instance = dropdown.multiSelectInstance;
        /* eslint-disable no-underscore-dangle */
        if (instance._events) {
          instance._events.forEach((event) => {
            (event.el || instance.dom.el).removeEventListener(
              event.name,
              event.fn,
              true
            );
          });
        }
        /* eslint-enable no-underscore-dangle */

        const siItems = dropdown.querySelectorAll(
          ".si-item, .si-result, .si-list"
        );
        siItems.forEach((item) => item.remove());

        dropdown.classList.remove("si-wrap", "si-off");
        // eslint-disable-next-line no-param-reassign
        dropdown.multiSelectInstance = null;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Error cleaning up MultiSelect instance:", e);
      }
    }

    this.initializeMultiselect(dropdown, resources, selectedIds);
  }

  toggleNoItemsMessage() {
    if (!this.hasNoItemsMessageTarget) {
      return;
    }

    const hasItems = Array.from(this.profileContainerTarget.children).some(
      (child) => child.tagName === "LI"
    );

    this.noItemsMessageTarget.style.display = hasItems ? "none" : "block";
  }

  addGroupedResource(rowIdOrEvent = null) {
    const clone = this.templateTarget.content.firstElementChild.cloneNode(true);

    const rowId = rowIdOrEvent instanceof Event ? null : rowIdOrEvent;
    clone.dataset.id =
      rowId || `row_${this.profileContainerTarget.children.length}`;
    const newNode = this.profileContainerTarget.insertBefore(clone, null);

    try {
      const dropdown = clone.querySelector(".resource-type-dropdown");
      if (dropdown) {
        dropdown.innerHTML = `<option value="" selected disabled>${
          this.selectOptionTextValue
        }</option>${this.resourceTypes
          .map(
            (resource) =>
              `<option value="${resource.id}">${resource.label}</option>`
          )
          .join("")}`;
      }

      const newIndex = this.groupedResourceTargets.length - 1;

      Array.from(newNode.querySelectorAll("label, select, input")).forEach(
        (element) => {
          const el = element;
          if (el.tagName === "LABEL") {
            const forAttr = el.getAttribute("for");
            if (forAttr) {
              const newVal = forAttr.replace("_ID_", `_${newIndex}_`);
              el.setAttribute("for", newVal);
            }
          } else {
            if (el.id && el.id.includes("_ID_")) {
              el.id = el.id.replace("_ID_", `_${newIndex}_`);
            }
            if (el.name && el.name.includes("[ID]")) {
              el.name = el.name.replace("[ID]", `[${newIndex}]`);
            }
          }
        }
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        "Error updating attributes in addGroupedResource:",
        e,
        clone
      );
    }

    this.toggleNoItemsMessage();
    return newNode;
  }

  loadState(data) {
    const existingRows = this.profileContainerTarget.querySelectorAll(
      "li.grouped-resource-row"
    );

    existingRows.forEach((row) => row.remove());

    this.state = {};

    data.grouped_resources.forEach((resource, index) => {
      const rowId = `row_${index}`;

      this.state[rowId] = {
        resources: new Set(),
        resourceSkus: new Set(),
        id: resource.id || null, // Store id for later use
      };

      const row = this.addGroupedResource(rowId);
      const resourceTypeSelect = row.querySelector(
        "[data-product-group-profile-target='resourceTypeSelect']"
      );
      if (resourceTypeSelect) {
        resourceTypeSelect.value = resource.resource_type_id;
        this.updateResources(
          { target: resourceTypeSelect },
          resource.resource_ids
        );
      }

      const resourceDropdown = row.querySelector(".resource-dropdown");
      if (resourceDropdown && resource.resource_ids) {
        const uniqueResourceIds = [
          ...new Set(resource.resource_ids.map((id) => parseInt(id, 10))),
        ];

        uniqueResourceIds.forEach((id) => {
          this.state[rowId].resources.add(id);
        });

        const uniqueResourceSkuIds = resource.resource_sku_ids
          ? [
              ...new Set(
                resource.resource_sku_ids.map((id) => parseInt(id, 10))
              ),
            ]
          : [];

        this.updateResourceSkus(
          { target: resourceDropdown },
          uniqueResourceSkuIds
        );
      }

      const resourceSkuDropdown = row.querySelector(".resource-sku-dropdown");
      if (resourceSkuDropdown && resource.resource_sku_ids) {
        const uniqueResourceSkuIds = [
          ...new Set(resource.resource_sku_ids.map((id) => parseInt(id, 10))),
        ];

        uniqueResourceSkuIds.forEach((id) => {
          this.state[rowId].resourceSkus.add(id);
        });

        const defaultResourceSkuId = resource.default_resource_sku_id
          ? parseInt(resource.default_resource_sku_id, 10)
          : null;

        this.updateDefaultResourceSkus(
          { target: resourceSkuDropdown },
          defaultResourceSkuId
        );
      }
    });

    this.toggleNoItemsMessage();
  }

  // TODO Maybe some of the form submit renaming logic could be moved into the template (?) depending on the Multiselect implementation
  handleFormSubmit(event) {
    this.clearValidationErrors();
    this.clearPreviousHiddenInputs();

    let isValid = true;
    const invalidRows = [];

    Object.keys(this.state).forEach((rowId, index) => {
      const rowState = this.state[rowId];
      const rowElement = this.element.querySelector(`[data-id="${rowId}"]`);
      if (!rowElement) return;

      const resourceTypeField = rowElement.querySelector(
        "[data-product-group-profile-target='resourceTypeSelect']"
      );
      const resourceTypeSelected =
        resourceTypeField &&
        resourceTypeField.value &&
        resourceTypeField.value !== "";

      const resources = Array.from(rowState.resources);
      const hasResources = resources.length > 0;

      const resourceSkus = Array.from(rowState.resourceSkus);
      let validResourceSkus = false;

      if (resourceSkus.length > 0 && hasResources) {
        validResourceSkus = resourceSkus.every((skuId) => {
          return resources.some((resourceId) => {
            const skus = this.resourceSkusByResource[resourceId] || [];
            return skus.some((sku) => sku.id === skuId);
          });
        });
      }

      const hasResourceSkus = resourceSkus.length > 0;

      const defaultResourceSkuField = rowElement.querySelector(
        "[data-product-group-profile-target='defaultResourceSkuSelect']"
      );
      const defaultResourceSkuId =
        defaultResourceSkuField && defaultResourceSkuField.value
          ? parseInt(defaultResourceSkuField.value, 10)
          : null;
      const validDefaultSku =
        defaultResourceSkuId && resourceSkus.includes(defaultResourceSkuId);

      if (
        !resourceTypeSelected ||
        !hasResources ||
        !hasResourceSkus ||
        (hasResourceSkus && !validResourceSkus) ||
        !validDefaultSku
      ) {
        isValid = false;
        invalidRows.push(rowElement);
        this.showValidationError(rowElement, {
          resourceType: !resourceTypeSelected,
          resources: !hasResources,
          resourceSkus:
            !hasResourceSkus || (hasResourceSkus && !validResourceSkus),
          defaultSku: !validDefaultSku,
        });
      }

      if (
        resourceTypeSelected &&
        hasResources &&
        hasResourceSkus &&
        validResourceSkus &&
        validDefaultSku
      ) {
        ProductGroupProfileController.clearOriginalFormFields(rowElement);

        if (rowState.id) {
          this.addHiddenInput(
            `product_group_profile[grouped_resources][${index}][id]`,
            rowState.id
          );
        }

        this.addHiddenInput(
          `product_group_profile[grouped_resources][${index}][resource_type_id]`,
          resourceTypeField.value
        );
        this.addHiddenInput(
          `product_group_profile[grouped_resources][${index}][default_resource_sku_id]`,
          defaultResourceSkuField.value
        );
        this.addHiddenInput(
          `product_group_profile[grouped_resources][${index}][resources]`,
          JSON.stringify(resources)
        );
        this.addHiddenInput(
          `product_group_profile[grouped_resources][${index}][resource_skus]`,
          JSON.stringify(resourceSkus)
        );
      }
    });

    if (!isValid) {
      event.preventDefault();
      if (invalidRows.length > 0) {
        invalidRows[0].scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }

  showValidationError(rowElement, errors) {
    if (errors.resourceType) {
      const el = rowElement.querySelector(
        '[data-product-group-profile-target="resourceTypeSelect"]'
      );
      if (el) {
        const errorDiv = document.createElement("div");
        errorDiv.className = "validation-error text-red-600 text-sm mt-1";
        errorDiv.innerText = this.resourceTypeValidationMessageValue;
        el.parentNode.appendChild(errorDiv);
      }
    }

    if (errors.resources) {
      const el = rowElement.querySelector(".resource-dropdown");
      if (el) {
        const errorDiv = document.createElement("div");
        errorDiv.className = "validation-error text-red-600 text-sm mt-1";
        errorDiv.innerText = this.resourcesValidationMessageValue;
        el.parentNode.appendChild(errorDiv);
      }
    }

    if (errors.resourceSkus) {
      const el = rowElement.querySelector(".resource-sku-dropdown");
      if (el) {
        const errorDiv = document.createElement("div");
        errorDiv.className = "validation-error text-red-600 text-sm mt-1";
        errorDiv.innerText = this.resourceSkusValidationMessageValue;
        el.parentNode.appendChild(errorDiv);
      }
    }

    if (errors.defaultSku) {
      const el = rowElement.querySelector(
        '[data-product-group-profile-target="defaultResourceSkuSelect"]'
      );
      if (el) {
        const errorDiv = document.createElement("div");
        errorDiv.className = "validation-error text-red-600 text-sm mt-1";
        errorDiv.innerText = this.defaultResourceSkuValidationMessageValue;
        el.parentNode.appendChild(errorDiv);
      }
    }
  }

  clearValidationErrors() {
    this.profileContainerTarget
      .querySelectorAll(".validation-error")
      .forEach((el) => el.remove());
  }

  static getSelectedItems(container) {
    const selectedItems = container.querySelectorAll(".si-item.si-selected");
    return Array.from(selectedItems).map((item) => ({
      key: item.dataset.key,
      value: item.textContent.trim(),
    }));
  }

  addHiddenInput(name, value) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    input.className = "js-dynamic-input"; // Add class for easy cleanup
    this.profileContainerTarget.closest("form").appendChild(input);
  }

  clearPreviousHiddenInputs() {
    const form = this.profileContainerTarget.closest("form");
    const dynamicInputs = form.querySelectorAll(".js-dynamic-input");
    dynamicInputs.forEach((input) => input.remove());
  }

  static clearOriginalFormFields(rowElement) {
    const originalFields = rowElement.querySelectorAll(
      "input[name*='grouped_resources'], select[name*='grouped_resources']"
    );

    for (let i = 0; i < originalFields.length; i += 1) {
      originalFields[i].name = "";
    }

    const allFormFields = rowElement.querySelectorAll(
      "input[name], select[name]"
    );

    for (let i = 0; i < allFormFields.length; i += 1) {
      const field = allFormFields[i];
      if (
        field.name &&
        (field.name.includes("grouped_resource") || field.name.includes("[ID]"))
      ) {
        field.name = "";
      }
    }
  }
}
